import React, { useCallback, useEffect, useState } from "react";

import { changeFreightFormRequestStatus } from "../../utils/api";
import { showError, showSuccess } from "../../utils/toast";
import { freightFormRequestStatusCodeFromRow } from "./freightFormRequestColumnCells";

const STATUS_OPTIONS = [
  { value: "0", label: "Pending" },
  { value: "1", label: "Complete" },
  { value: "2", label: "Rejected" }
];

/** Matches freight admin form controls (`adminFreight.css` / order forms): rounded, subtle border, brand focus. */
const selectClass =
  "block w-full min-w-[118px] max-w-[168px] cursor-pointer appearance-none rounded-lg border border-[#dfe3ea] " +
  "bg-white py-1.5 pl-2.5 pr-8 text-left text-[13px] font-medium text-gray-800 shadow-sm " +
  "transition-[border-color,box-shadow] duration-150 hover:border-gray-300 " +
  "focus:border-[#064E8D] focus:outline-none focus:ring-2 focus:ring-[#064E8D]/15 " +
  "disabled:cursor-not-allowed disabled:opacity-60";

const FreightFormRequestStatusColumn = ({ row, onUpdated }) => {
  const id = row?.id;
  const type = row?.type != null ? String(row.type).toLowerCase() : "";
  const [busy, setBusy] = useState(false);
  const [localStatus, setLocalStatus] = useState(() => freightFormRequestStatusCodeFromRow(row));

  useEffect(() => {
    setLocalStatus(freightFormRequestStatusCodeFromRow(row));
  }, [row]);

  const onChange = useCallback(
    async (e) => {
      const next = String(e.target.value);
      if (id == null || (type !== "buyer" && type !== "seller")) return;
      const prev = freightFormRequestStatusCodeFromRow(row);
      setLocalStatus(next);
      setBusy(true);
      try {
        await changeFreightFormRequestStatus({
          type,
          id,
          status: Number(next),
          remarks: row?.remarks != null ? String(row.remarks) : ""
        });
        showSuccess("Status updated");
        onUpdated?.();
      } catch (err) {
        setLocalStatus(prev);
        showError(err.message || "Status update failed");
      } finally {
        setBusy(false);
      }
    },
    [id, onUpdated, row, type]
  );

  if (id == null || (type !== "buyer" && type !== "seller")) {
    return <span className="text-gray-400 text-xs">—</span>;
  }

  return (
    <div className="relative w-full max-w-[168px]">
      <select
        className={selectClass}
        value={localStatus}
        disabled={busy}
        onChange={onChange}
        aria-label="Form request status"
      >
        {STATUS_OPTIONS.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      <span
        className="pointer-events-none absolute inset-y-0 right-0 flex w-8 items-center justify-center text-gray-500"
        aria-hidden
      >
        <i className={`fas fa-chevron-down text-[10px] ${busy ? "opacity-40" : ""}`} />
      </span>
    </div>
  );
};

export default FreightFormRequestStatusColumn;
